<script setup>
import { ref, watch, onMounted, inject} from 'vue'
import { Chart } from 'highcharts-vue'
import Highcharts, { isNumber } from 'highcharts'
import {defineMapProps} from './MapSetup';
import * as Accessibility from "@/components/accessibility/accessibility.js";

//componenet methods exposed to parent
defineExpose({
	setupMap,
	refreshMap,
	toggleMapDataLabels
});


//events raised by component
const emit = defineEmits(["setMapObj","openFootnoteModal", "toggleSkeletonLoader"])
const { userInputState } = inject('userInputState')
const { mapDrilldownState } = inject('mapDrilldownState')

// --- Reactive chart state ---
//const currentLevel = ref(0)
const chartOptions = ref({})
const chartRef = ref({})
const toggleDataLabel = ref(false)


//Technique for forcing Highcharts to destroy existing chart and create a new chart instead of updating existing chart
//Changing chart :key property triggers HC to create new chart instance
const mapKey = ref(0)

//local variables
let geoMap = null
let mapLevel = null
let mapLevelsCnt = 0
let indObj = null
let chartSettings = null
let apiData = null
let parentSelectionState = []

// Breadcrumb state
const breadcrumb = ref([])

async function fetchFile(url) {
	try {
		// Make the fetch request and wait for the response
		const response = await fetch(url);

		// Check if the response was successful (status code 200-299)
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		// Parse the response body as JSON and wait for the result
		const data = await response.json();

		// Return the fetched data
		return data;
	} catch (error) {
		// Handle any errors that occurred during the fetch or parsing
		console.error('Error fetching data:', error);
		// Re-throw the error or return a default value/null depending on your needs
		throw error;
	}
}

function onMoreClick(e) {
  emit("openFootnoteModal",e)
}

async function setupMap(indicator, settings) {
	indObj = indicator
	breadcrumb.value = []
	userInputState.value.geo = false;

	try {
		//let urlTopMap = 'map-collection/countries/us/us-all.topo.json'
		let mapProperties = await fetchFile(import.meta.env.VITE_BASE_URL + '/json/map-properties.json')
		//console.log('mapProperties', mapProperties)
		settings.mapProps = mapProperties.find(p => p.indicator.toLowerCase() == indicator.indicatorName.toLowerCase())

		mapLevel = settings.mapProps.levels[0]
		if(mapLevel == null)
			throw new Error('map properties for indicator not found')
		geoMap = await loadMap(mapLevel.maps[0].map)
		mapLevelsCnt = settings.mapProps.levels.length

		if(mapLevel.navTitle)
			breadcrumb.value.push({ crumbName: mapLevel.navTitle})

		if(mapLevel.noData) {
			chartSettings = settings
			apiData = {}
			if(import.meta.env.VITE_APP_DEBUG){
				console.log('nodata chartSettings', chartSettings)
			}			
		} else {
			const results = await defineMapProps(indicator, settings, mapLevel, true)
			chartSettings = results.chartSettings
			apiData = results.dataResults
			if(import.meta.env.VITE_APP_DEBUG){
				console.log('chartSettings', chartSettings)
				console.log('map data', apiData)
			}
			//console.log('mapLevel', mapLevel);
		}
		setHCProps(apiData, geoMap, mapLevel)
	} catch (error) {
		console.log('Failed to get and display data: ' + error);
		throw error;
	}
}

// --- Helper to load topo data ---
async function loadMap(map) {
  	const mapData = await fetchFile(map)
	let geoMap;
	if(map.endsWith('.topo.json')) {
		const objName = Object.keys(mapData.objects)[0]
		geoMap = Highcharts.topo2geo(mapData, objName)
	} else {
		geoMap = mapData
	}
	//console.log('geoMap', geoMap)
  	return geoMap
}

async function refreshMap(indicator, settings, filteredDim) {
	indObj = indicator
	try {
		//emit("toggleSkeletonLoader",true)
		const results = await defineMapProps(indicator, settings, mapLevel)
		chartSettings = results.chartSettings
		apiData = results.dataResults

		//console.log('Map Filter Data', apiData);
		const chart = chartRef.value.chart
		let geoMap = chart.mapView.geoMap

		let updSeries = buildSeries(geoMap, apiData, mapLevel)
    	let chartOptions = {
			title: { text: ''},
			subtitle: { text: '', y: 46},
			series: updSeries
		}

		buildHeader(chartOptions)
    	buildFooter(chartOptions)
		chart.update(chartOptions, true, true)

		//Update URL with updated filters
		let filters = []
		filteredDim.appliedValues.forEach(f => {
			if(f.selected)
				filters.push(f.name)
		})
		const cat = chartSettings.request.columns.find(col => col.columnName == filteredDim.columnName)
		cat.filters = filters
	} catch (error) {
		console.log('Failed to get and display data: ' + error);
		throw error;
	} finally {
		//emit("toggleSkeletonLoader",false)
	}
}

function gotoDataNotes() {
	// footnote link onClick event fires a click event on the Data Notes menu item
	const menuItems = document.querySelector('.viz-menu-container').querySelectorAll('.menu-item')
	if(menuItems != null) {
		menuItems.forEach(item => {
			if (item.innerText.trim() == 'Data Notes') {
				item.click()
			}
		})
	}  
}
const onChartInit = (chartInstance) => {
	// adding an eventListener to "See full data notes" link
	const footerLinkElement = chartInstance.container.querySelector('.explorer-footnote-link')
	if (footerLinkElement) {
		footerLinkElement.addEventListener('click', () => {
			gotoDataNotes() 
		})
	}
}

function setHCProps(data, geoMap, mapLevel) {
	//const usdFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2});
	const pctFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indObj.decimalPrecision,maximumFractionDigits: 2});
	const floatFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indObj.decimalPrecision, maximumFractionDigits: 100});
	const numFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 100});
	const valuesUnit = indObj.valuesUnit; // == "Currency"
	//TODO: Enable new formatter logic if Float valuesUnit implemented
	let formatter = valuesUnit == "Currency"?numFormatter:valuesUnit == "Percent"?pctFormatter:valuesUnit=="Float"?floatFormatter:numFormatter
	//let formatter = valuesUnit == "Currency"?numFormatter:valuesUnit == "Percent"?pctFormatter:numFormatter

	//const valuesMultiplier = (indObj.chartMultiplier ? parseInt(indObj.chartMultiplier) : 1);
	const valuesRoundingUnit = (indObj.roundingUnit && indObj.roundingUnit.length > 0 ? '(' + indObj.roundingUnit.replace(/[$%]/g, "") + ')' : null);

	mapKey.value++
	let chartprops = {
		chart: {
			styledMode: false, //use fill setting
			//styledMode: true, //override fill setting with css styles
			map: geoMap,
			height: 560,
			//height: 0,
			events: {
				load() {
					onChartInit(this);
				},
				render() {
					//add Full Source button link to footer to support mobile format
					const links = this.container.querySelectorAll('.footnote-more');
					if(links != null) {
						links.forEach(link => {
							if (link && !link.dataset.bound) {
								link.dataset.bound = 'true';

								link.addEventListener('click', (e) => {
									e.preventDefault();
									console.log('clicked', e)
									onMoreClick(e)
								});
							}
						})
					}

					//reformat title/subtitle to support wrapped labels in mobile layout
					const chart = this;
					if (!chart.title || !chart.subtitle) {
						return;
					}
					const titleBBox = chart.title.getBBox();
					if(titleBBox.height < 30) {
						chart.subtitle.attr({
							y: chart.title.alignAttr.y + titleBBox.height 
						});
					} else {
						chart.title.attr({
							y: titleBBox.height - 50
						});						
						chart.subtitle.attr({
							y: titleBBox.height
						});
					}								
				}
			}
		},
		credits: {
			enabled: false
		},
		title: { text: ''},
		subtitle: { text: '', y: 46},
		mapNavigation: { enabled: true, buttonOptions: { align: 'right', verticalAlign: 'bottom' } },
 		plotOptions: {
			map: {
				allAreas: true,
				borderColor: '#ffffff', //'#606060',
				borderWidth: 0.5,
				states: {
					hover: {
						brightness: 0,
						enabled: true,
						borderColor: '#FFF8DC',
						borderWidth: 1						
					}
				}
	        }
		},	
		series: buildSeries(geoMap, data, mapLevel),
		legend: {
			enabled: mapLevel.noData?false:true,  //true,
			align: 'center',
			verticalAlign: 'top',
			zIndex: 1,
			floating: false,
			layout: 'horizontal',
			useHTML: true,
			labelFormatter: function () {
				return `
					<div style="display:flex; flex-direction: column;line-height: 1.25em;font-size: var(--font-size-Base);
						height: 2.5em; justify-content: center;">
						<div style="">
							${this.name}
						</div>
						<div><span style="font-size: .75; opacity: .7;">${this.userOptions.range == null?'':this.userOptions.range}</span>
						</div>
					</div>
				`;
			}
		},
		//This tooltip setting will override the plot:tooltip settings
 		tooltip: {
			shared: true,
			useHTML: true,
			backgroundColor: '#a9b7c7',
			style: {
				zIndex: 9999   // force tooltip above legend
			},
			formatter: function () {
				if (this.point.custom?.disabled) {
					return false;
				}
				const yValue = indObj.valuesLabel; //this.point.value
				let header =`<div class="header"><span class="title">${yValue} ${valuesRoundingUnit}</span></div>`;

				//const value = indObj.valuesUnit == "Currency" ? usdFormatter.format(Math.round(this.value)) : numFormatter.format(Math.round(this.value));

				let toolTips = ""
				if(mapLevel) {
					if(mapLevel.columnTotal) {
						const item = chartSettings.columnSubTotals.find(x => x.tipGrp == mapLevel.columnTotal)
						if(item && item.tipValue && item.tipGrp) {
							let tipValue = formatter.format(item.tipValue)
							//tipValue += indObj.valuesUnit == "Percent" ? '%' : ''
							toolTips += '<div class="item">'+item.tipGrp+'</span>: <b>'+tipValue+'</b></div>'
						}
					}
					if(this.tipLink) {
						let items = []
						let sharedAreas = this.tipLink.split(";")
						if(this.tipLink.includes(';'))
							sharedAreas.push(this.tipLink)
						sharedAreas.forEach(y => {
							chartSettings.columnSubTotals.forEach(x => {
								if(x.tipLink == y.trim())
									items.push(x)
							})
						})

						items.forEach(item => {
							let tipValue = formatter.format(item.tipValue)
							//tipValue += indObj.valuesUnit == "Percent" ? '%' : ''
							toolTips += '<div class="item">'+item.tipGrp+'</span>: <b>'+tipValue+'</b></div>'
						})


					}
				}

				//let tipValue = indObj.valuesUnit == "Currency" ? usdFormatter.format(this.value) : numFormatter.format(this.value);
				let tipValue = formatter.format(this.value)
				//tipValue += indObj.valuesUnit == "Percent" ? '%' : ''
				let currentTip = `<div class="item"><b>${this.name}</b>: <b> ${tipValue}</b></div>`
				let body = toolTips + currentTip
				return '<div class="map explorer-tool-tip">' + header + body + '</div>';
			},
			positioner: function (labelWidth, labelHeight, point) {
				const chart = this.chart;
				let x = point.plotX + chart.plotLeft + 24;
				let y = point.plotY + chart.plotTop - (labelHeight / 2);

				// Prevent tooltip from going outside the right edge
				if (x + labelWidth > chart.chartWidth) {
				x = point.plotX + chart.plotLeft - labelWidth - 24;
				}

				return { x, y };
			}
		},
		exporting: {
			enabled: true, // still needed for export functionality
			libURL: 'highcharts-libs/',
			local: true,
			fallbackToExportServer: false, // use offline export only
			chartOptions: {
				chart: {
					type: 'map',
					style: {
						//prevents warning on fetching font files that are not needed
						fontFamily: 'Roboto,sans-serif'
					},
					//spacingBottom: 10
				},				
				title: {
					style: {
						fontSize: '1.25rem', //'var(--font-size-H6, 1.25em)',
						color: '#2C2C2C' //'var(--text-Header)'
					},
					//spacingBottom: 10
				},
				legend: {
					useHTML: false,
					itemStyle: {
						fontSize: '12px', //'var(--font-size-Base, 1em);',
						color: '#2C2C2C'  //'var(--text-Primary, #2C2C2C);',
					},
					title: {
						style: {
							fontSize: '16px', //' var(--font-size-Base, 1em);',
							color: '#2C2C2C'   //'var(--text-Primary, #2C2C2C);',
						}
					},
					labelFormatter: function () {
						return `
							<div style="display:flex; flex-direction: column;line-height: 1.25em;
								height: 2.5em; justify-content: center;">
								<div style="">
									<b>${this.name}</b>${this.userOptions.range == null?'':': ' + this.userOptions.range}
								</div>
							</div>
						`;
					}					
				},
				series: [{
					dataLabels: {
						style: {
							fontSize: '16px'
						}
					}
				}],
			},
			buttons: {
			 	contextButton: {
			 		enabled: false // hides the built-in button
			 	}
			},
 			error: function(options, err) {
				console.log('Highcharts Export Error:', err);
				console.log('Exporting options:', options);
			},
		},
		//caption: buildFooter()
	}

	buildHeader(chartprops)
	buildFooter(chartprops)
	chartOptions.value = chartprops

	if(import.meta.env.VITE_APP_DEBUG)
		console.log('map chartProps', chartprops)	
}

function buildHeader(chartprops) {
	//let geoCol = indObj.columns.items.find(x => x.geoLabelFlag == 1 && x.parentFlag == 0)
	//let title = `${indObj.valuesLabel} by ${geoCol?.columnName}`
	let title = `${indObj.valuesLabel}`
	let year = indObj.columns.items.find(x => x.columnName == 'Year')
	if(year) {
		const minYear = Math.min(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		const maxYear = Math.max(...year.appliedValues.filter(x => x.selected).map(item => Number(item.name)));
		if(minYear == maxYear)
			title += ": " + minYear
		else
			title += ": " + minYear + "\u2013" + maxYear
	}
	chartprops.title.text = title

	let filters = ''
	let cols = 	indObj.columns.items.filter(c => {
		if(c.valueFlag == 0 && c.categoryLabelFlag == 1 && c.columnName != 'Year'
			&& c.geoLabelFlag == 0
			&& c.parentFlag == 0) {
				return c
			}
		})
	cols.forEach((c, n) => {
		let selected = "<span class='section-break'>" + c.columnName + ": "
		if(c.appliedValues && c.appliedValues.length > 0) {
			let all = c.totalLabel 
			let values = c.appliedValues.filter(x => x.selected && x.name != all)
			if(values.length > 0) {
				values.forEach((v, i) => selected += v.name + (i < values.length-1?', ': n < cols.length -1?'; ':''))
				filters += selected + "</span>"
			}
		}
	}) 
	chartprops.subtitle.text = filters	
}

function findMinMax(arr) {
	if (!Array.isArray(arr) || arr.length === 0) {
		return { min: null, max: null };
	}

	let minObj = arr[0];
	let maxObj = arr[0];

	for (const item of arr) {
		if (item < minObj) {
		minObj = item;
		}
		if (item > maxObj) {
		maxObj = item;
		}
	}

	return { min: minObj, max: maxObj };
}

//Use this, shows Series data points, comment out to test gradient
function  buildSeries(geoMap, data, mapLevel) {
	//mapLevel.noData optional property in map-properties.json that indicates a parent level has no data but maps should
	//still allow users to click on an area to drilldown to child level which will have data. 
	//Example, Map of USA where each state does not have a total that can be displayed in the tooltip when user mouses over a state,
	//but stil need capabiltity to drilldown to the state to see county level data. 
	if(mapLevel.noData) {
		return buildEmptySeries(geoMap, mapLevel)
	} else {
		return buildDataSeries(geoMap, data, mapLevel)
	}
}

function rgbToHex(rgb) {
    const [r, g, b] = rgb.match(/\d+/g).map(Number);
    return '#' + [r, g, b]
        .map(x => x.toString(16).padStart(2, '0'))
        .join('');
}

//version of method shows all states in single color since states are mising values
function  buildEmptySeries(geoMap, mapLevel) {
	const mapKey = mapLevel.mapKey

	//extract null color code from highcharts CSS variable to use for each state
	const root = document.documentElement;
	const styles = getComputedStyle(root);
	const nullColor = styles.getPropertyValue('--brand-color-BlueGrey').trim(); //#c6c2c2;

	const series = [
	{
		type: 'map',
		name: 'States',
		mapData: geoMap,
		joinBy: mapKey,



		//set state value to non-null so that it is clickable for drilldown
		data: geoMap.features.map((state, index) => ({
			[mapKey]: state.properties[mapKey],
			value: index // 1, //Math.round(Math.random() * 100)
		})),

		color: nullColor,
		fill: nullColor,
		className: 'indicators-map-series indicators-map-series-null',
		showInLegend: false,
		dataLabels: {
			enabled: true,
			formatter: function () {
				return this.point.properties['postal-code'];
			},
			style: {
				textOutline: 'none'
			}
		},
		point: {
			events: {
				click: async function (e) {
					await drillDown(e)							
				},
			}				
		},
		states: {
			hover: {
				enabled: true,
				color: 'f7f7f7',
				borderColor: '#606060',
				borderWidth: .2
			}
		}
	}]
	console.log('stateData', series[0].data)
	return series;
}

function  buildDataSeries(geoMap, data, mapLevel) {
	const valuesUnit = indObj.valuesUnit; // == "Currency"
	//const usdFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2});
	const pctFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indObj.decimalPrecision,maximumFractionDigits: 2});
	const numFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 100});
	const floatFormatter = new Intl.NumberFormat('en-US', {minimumFractionDigits: indObj.decimalPrecision, maximumFractionDigits: 100});


	//const valuesMultiplier = (indObj.chartMultiplier ? parseInt(indObj.chartMultiplier) : 1);
	//const valuesRoundingUnit = (indObj.roundingUnit && indObj.roundingUnit.length > 0 ? '(' + indObj.roundingUnit.replace(/[$%]/g, "") + ')' : null);
	//const classNameBase = Accessibility.readUserPreference('patternfill') === 'true' ? 'indicators-chart-series-dashed-' : 'indicators-chart-series-';
	let formatter = valuesUnit == "Currency"?numFormatter:valuesUnit == "Percent"?pctFormatter:valuesUnit=="Float"?floatFormatter:numFormatter

	const quartiles = quartilesMapByProp(data, indObj.valuesLabel);

	//console.log('quartiles', quartiles)
	let dataKey = mapLevel.dataKey
	let mapKey = mapLevel.mapKey
	let linkFld = mapLevel.groupColumnName

	//add a map with no data as the 1st series so that an outline of the entire map is displayed
	const series = [
		{
			name: 'No data',
			mapData: geoMap,
			color: '#a9b7c7', //'#E6E6E6',
			showInLegend: true,
			enableMouseTracking: false,
			allAreas: true,
    		nullColor: '#a9b7c7',  //Color overwritten by .highcharts-series-0.highcharts-map-series .highcharts-null-point
			joinBy: mapKey,
			data: [],		
			legendIndex: 5
		}
	]

	//add a series for each category to be displayed as a map section
	let cnt = 0
	for (let x in quartiles.groups) {
		if(quartiles.groups[x].length > 0)
			cnt++
	}
	let code
	for (let x in quartiles.groups) {
		if(quartiles.groups[x].length > 0) {
			//Q0=blue50, Q1=blue200, Q2=blue400, Q3=blue600, Q4=blue800, none=blueGrey
			code = x=='Q0'?0:x=='Q1'?1:x=='Q2'?2:x=='Q3'?3:4
			//seriesClass is used for html rendition, since images use svg that can not use classes,
			//color property is used for export to image
			let seriesClass = 'indicators-map-series indicators-map-series-' + code;
			if(mapLevelsCnt > 1 && mapLevel.level < mapLevelsCnt -1)
				seriesClass += ' indixators-map-series-pointer'
			let colorCode = x=='Q0'?'#ecf3fc':x=='Q1'?'#cce4ff':x=='Q2'?'#79a9e1':x=='Q3'?'#3a77b1':'#135283'
			let rangeValue = ''
			quartiles.groups[x].length


			if(x != 'Q0') {
				let minMax = findMinMax(quartiles.groups[x].map(c => c[1]))
				if(quartiles.groups[x].length > 1) {
					rangeValue = formatter.format(minMax.min) + ' - ' + formatter.format(minMax.max)
				} else {
					rangeValue = formatter.format(minMax.min)
				}
			}
			let s = {
				joinBy: mapKey,
				allAreas: false,
				name: cnt < 4?'':x=='Q0'?'Zero':'Quartile '+ (x=='Q1'?1:x=='Q2'?2:x=='Q3'?3:x=='Q4'?4:''),
				range: rangeValue,
				className: seriesClass,
				color: colorCode,
				data : getData( mapKey, quartiles.groups[x], data[dataKey], linkFld?data[linkFld]:null),
				events: {
					click: async function (e) {
						//const key = e.point.properties['hc-key']
						await drillDown(e)
					}
				},
				dataLabels: {
					enabled: false, //toggleDataLabel.value,
 					formatter: function () {
						// show label ONLY if value is not null
						if (this.point.value !== null && this.point.value !== undefined) {
							return this.point.name;
						}
						return ''; // hide label
					},
					style: {
						textOutline: 'none'
					}					
				},
				states: {
					hover: {
						enabled: true,
						color: '#fac0ae',
            borderWidth: 1,
            borderColor: '#666'						
					}
				}				
			}
			series.push(s)
		}
	}
	return series;
}

function getData(mapKey, grp, keys, linkFlds) {
	// console.log('getData mapKey', mapKey)
	// console.log('getData grp', grp)
	// console.log('getData keys', keys)
	let data = []
	for (let x = 0; x < grp.length; x++) {
		let indx = grp[x][0]
		let dataKey = keys[indx]
		let val = grp[x][1];
		let link = linkFlds?linkFlds[indx]:null
		let o = {}
		o[mapKey] = dataKey;
		o["value"] = val;
		if(link)
			o.tipLink = link

		data.push(o)

	}
	return data
}

function quartilesMapByProp(data, prop) {
	if(data == null)
		return {}.groups = []

	let tmp = []
	Object.entries(data[prop]).forEach((entry) => {
		if (typeof entry[1] === 'number' && !isNaN(entry[1])) {
			tmp.push(entry)
		} else if (typeof entry[1] === 'string') {
			// Remove commas to handle formatted numbers like '1,444,050'
			const cleanedString = entry[1].replace(/[,.]/g, '').trim();
			// Check if the cleaned string is a valid number and not empty
			if(cleanedString !== '' && !isNaN(Number(cleanedString))) {
				tmp.push([entry[0], Number(cleanedString)])
			}
		}
	});

	const mapData = new Map(
		tmp.map(([key, value]) => [Number(key), Number(value)])
	);
	//console.log('rows', tmp.length)
	const sorted = sortMapDataByValue(mapData);

	const sortedValues = [...sorted.values()];
	//console.log('sortedValues',  sortedValues.length)
	const zeroValues = sortedValues.filter(x => x == 0.0)
	const nonZeroValues = sortedValues.filter(x => x > 0.0)
	//console.log('zeroValues',  zeroValues.length)
	const n = sortedValues.length - zeroValues.length;
	//console.log('quartile cnt', n)
	const qz = zeroValues.length; //0 or sortedValues[Math.floor(0.0)];
	const q1 = nonZeroValues[Math.floor(0.25 * n)];
	const q2 = nonZeroValues[Math.floor(0.50 * n)];
	const q3 = nonZeroValues[Math.floor(0.75 * n)];
	//console.log('qz', qz)
	//console.log('q1', q1)
	//console.log('q2', q2)
	//.log('q3', q3)


	// 3. Create groups of objects
	const groups = {
		Q0: [],
		Q1: [],
		Q2: [],
		Q3: [],
		Q4: []
	};

	for (const obj of sorted) {
		const v = obj[1];
		if (v <= 0) groups.Q0.push(obj);
		else if (v <= q1) groups.Q1.push(obj);
		else if (v <= q2) groups.Q2.push(obj);
		else if (v <= q3) groups.Q3.push(obj);
		else groups.Q4.push(obj);
	}

	return {
		breakpoints: { q1, q2, q3},
		groups
	};
}


function sortMapDataByValue(map) {
	return new Map([...map.entries()].sort((a, b) => a[1] - b[1]));
}

async function drillDown(e) {
	let nextLevel = chartSettings.mapProps.levels[mapLevel.level+1] 
	if(nextLevel == null)
		return

	if(import.meta.env.VITE_APP_DEBUG){
		console.log('drilldown indicator', indObj)
	}

	try {
		emit("toggleSkeletonLoader",true)
		//store selection state of parent values before setting selection to the drilldown vale, theses states will be restored on drill up
		parentSelectionState = []
 		const parent = indObj.columns.items.find(x => x.columnName == mapLevel.columnName)
		parent.appliedValues.forEach(x => {
			let obj = {name: x.name, selected:x.selected?true:false}
			obj.appliedValues = x.appliedValues.map(z => {
				return {name: z.name, selected: z.selected, parentValue: z.parentValue}
			})
			parentSelectionState.push(obj)
			if(x.name == e.point.key) {
				x.selected = true
				x.appliedValues.forEach( z => z.selected = true)
			} 
			else {
				x.selected = false
				x.appliedValues.forEach( z => z.selected = false)		
			}
		})

		let key = e.point.properties['hc-key']
		mapLevel = nextLevel
		let mapPath = mapLevel.maps[0].map

		let areaCode ="" 
		if(key.startsWith("us"))
			areaCode = "us"
		else {
			areaCode = key
			key = key + "-all"
		}
		mapPath = mapPath.replace('[AREATAG]',areaCode)
		mapPath = mapPath.replace('[TAG]',key)		
		let geoMap = await loadMap(mapPath)
		breadcrumb.value.push({ crumbName: e.point.name })
		const retObj = await defineMapProps(indObj, chartSettings, mapLevel,false)
			chartSettings = retObj.chartSettings
			apiData = retObj.dataResults
			if(import.meta.env.VITE_APP_DEBUG){
				console.log('chartSettings', chartSettings)
				console.log('map data', apiData)
			}
			//console.log('mapLevel', mapLevel);
		
		setHCProps(apiData, geoMap, mapLevel)

		userInputState.value.geo = true;
		//raise event to update address bar	with the updated URL properties
		if (window.routedComponent && typeof window.routedComponent.updateUrlParms === 'function') {
			window.routedComponent.updateUrlParms();
		} else {
			console.warn('Router component not available yet.');
		}
		
 		if (window.routedComponent && typeof window.routedComponent.openFiltersPanel === 'function') {
			const area = parent.appliedValues.find(x => x.selected)?.name
			mapDrilldownState.value.dim = parent.childColumnName;
			mapDrilldownState.value.selectedArea = area;			
			window.routedComponent.openFiltersPanel(true);
		} else {
			console.warn('Router component not available yet.');
		}

		//console.log('mapLevels',mapLevels)
	} catch (err) {
		console.error('Error loading state topo:', err)
	} finally {
		emit("toggleSkeletonLoader",false)
	}
}

async function drillUp() {
	try {
		emit("toggleSkeletonLoader",true)
		breadcrumb.value.pop();
		mapLevel = chartSettings.mapProps.levels[mapLevel.level-1] 
		if(mapDrilldownState.value.dim){
			mapDrilldownState.value.dim = null
			mapDrilldownState.value.selectedArea = null
		}

		//restore parent selections to selection state prior to drilldown
 		const parent = indObj.columns.items.find(x => x.columnName == mapLevel.columnName)
		parent.appliedValues.forEach(x => {
			let obj = parentSelectionState.find(y => y.name == x.name)
			if(obj)
				x.selected = obj.selected
				x.appliedValues = obj.appliedValues
		})

		userInputState.value.geo = false;
		const geoMap = await loadMap(mapLevel.maps[0].map)
		const retObj = await defineMapProps(indObj, chartSettings, mapLevel, true)
		chartSettings = retObj.chartSettings
		apiData = retObj.dataResults

		setHCProps(apiData, geoMap, mapLevel)
 		if (window.routedComponent && typeof window.routedComponent.refreshFilter === 'function') {			
			window.routedComponent.refreshFilter(chartSettings);
		}		
	} catch (err) {
		console.error('Error loading state topo:', err)
	} finally {
		emit("toggleSkeletonLoader",false)
	}
}

function buildFooter(chartprops) {
	const abbr = indObj.abbreviations ? indObj.abbreviations.join('; ') + '.' : '';
	const src = indObj.source;
	//srcHtml is used in the footer mobile modal
	chartSettings.srcHtml = src
	chartSettings.keyHtml = indObj.abbreviations ? indObj.abbreviations.join('.<br />') + '.' : '';

	let footerNotes = `<div class="explorer-footnote-wrapper">`
	if(indObj.abbreviations && indObj.abbreviations.length > 0) {
		footerNotes += `	<span class="explorer-footnote"><span class="title">Key:</span> ${abbr} </span>`
		footerNotes += `	<button class="btn-tertiary footnote-more" data-value='abbreviations'>Full Key</button><br />`
	}
	footerNotes += `	<button class="btn-tertiary explorer-footnote-link" data-value='source'>See full data notes</button><br/>`
	footerNotes += `	<span class="explorer-footnote"><span class="title">Source(s):</span> ${src} </span>`
	footerNotes += `	<button class="btn-tertiary footnote-more" data-value='source'>Full Source</button>`
	footerNotes += `</div>`

  	let footerNotesExp = `<div class="explorer-footnote-wrapper">`
	if(indObj.abbreviations && indObj.abbreviations.length > 0) {		
		footerNotesExp += `<span class="explorer-footnote"><span class="title">Key:</span> ${abbr} </span><br/>`
	}
	footerNotesExp += `<span class="explorer-footnote"><span class="title">Source(s):</span> ${src} </span></div>`

  	let footer = {}
	footer.style = {color: '#000000', fontSize: '12px'},
	footer.align = "left"
	footer.verticalAlign = "bottom"
	footer.useHTML = true
	footer.text = footerNotes
	chartprops.caption = footer;

	let footerExp = {}
	footerExp.align = "left"
	footerExp.verticalAlign = "bottom"
	footerExp.useHTML = false
	footerExp.text = footerNotesExp
	if(chartprops.exporting == null) {
		chartprops.exporting = {}
	}
	if(chartprops.exporting.chartOptions == null) {
		chartprops.exporting.chartOptions = {}
	}
	chartprops.exporting.chartOptions.caption = footerExp;

	return;
}

function toggleMapDataLabels(value) {
	toggleDataLabel.value = value
}

function toggleLabels(showLabels) {
  	const chart = chartRef.value.chart
	chart.series.forEach(s => {
		if(s.name != 'No data') {
			s.update({
 				dataLabels: {
					enabled: showLabels,
					formatter: function () {
						if (this.point.value !== null && this.point.value !== undefined) {
							if(this.point.properties && this.point.properties[mapLevel.dataLabel])
								return this.point.properties[mapLevel.dataLabel]
							else
								return '';
						}
						return '';
					}
				}
			}, true, true) // <--- Set redraw=true, oneToOne=true
		}
	})
}

onMounted(() => {

	watch(toggleDataLabel,(value) => {
		toggleLabels(value)
	});

	watch(chartRef, (newValue) => {
		if(newValue != null) {
			emit('setMapObj', chartRef.value.chart)
		}
	})
})

</script>

<template>
	<div class="chart-options-row">
		<div v-if="breadcrumb.length > 1" class="breadcrumb-container">
			<span v-for="(crumb, i) in breadcrumb" :key="i">
				<button :disabled="i == breadcrumb.length - 1"
					class="btn-tertiary"
					@click="drillUp(i)">
					{{ crumb.crumbName }}
				</button>
				<span v-if="i < breadcrumb.length - 1"> / </span>
			</span>
		</div>
	</div>
	<div class="highcharts-light">
		<div style="height:584px" class="map">
			<Chart :options="chartOptions" :key="mapKey"
				:constructor-type="'mapChart'"
				ref="chartRef" />
		</div>
	</div>
</template>

<style>
/*.map {
	min-height: 48vh;
}*/
.map .highcharts-label {
  fill: #ffffff;
  fill-opacity: 1;
  stroke: #999999;
  stroke-width: 1px;
}

/*  tooltip  format*/
.map .highcharts-tooltip.indicators-map-series > span {
    background-color: #fff;
	padding: 8px;
    border: 1px solid #ccc;
	font-size: var(--font-size-chart-Tooltip) !important;
}

.map .highcharts-tooltip path {
    stroke-width: 0;
}

.map g.highcharts-tooltip path {
    opacity: 0;
}
.map .explorer-tool-tip {
	background-color: var(--surface-color-Default);
	padding: 0.5rem;
	font-size: var(--font-size-chart-Tooltip);
}

.map .explorer-tool-tip div:nth-child(2) {
	margin-top: .5rem;
}

.map .tooltip-icon {
	padding: 0 4px 0 0;
	font-size:1.25em;
}

g.highcharts-series, .highcharts-markers, .highcharts-point {
    outline: none;
    stroke: none;
}

.highcharts-map-series .highcharts-point-hover {
	stroke-width: 0!important; /* .07px */
	color:#ffffff;
} 

.explorer-footnote {
	color: var(--text-Secondary, #757575);

	/* Regular/Base */
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base);
	font-style: normal;
	font-weight: 400;
	line-height: var(--font-line-height-Base); /* 150% */
}

.indixators-map-series-pointer {
	cursor: pointer;
}

.highcharts-caption .footnote-more {
    display: none;
}

@container (max-width: 965px) {
	.explorer-footnote-wrapper {
		text-wrap: nowrap;
	}

	.highcharts-caption .explorer-footnote,
	.table-footnote .explorer-footnote {
		text-wrap: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: inline-block;
        width: 80%;
	}

	.highcharts-caption .footnote-more,
	.table-footnote .footnote-more {
		display: inline;
		margin-left: .75rem;
        padding: 0;
        vertical-align: top;
        height: 1.5rem;
		min-width: 2rem;
		min-height: unset;
		text-align: start;
	}
}

@container (max-width: 499px) {
	.highcharts-caption .explorer-footnote,
	.table-footnote .explorer-footnote {
        width: 70%;
	}
}
</style>
<style scoped>
	/*
	:root {
		--highcharts-neutral-color-3: #cccccc;
	}
	*/

	.chart-options-row {
		display:flex;
		flex-direction: row;
		justify-content: space-between;
		column-gap: var(--spacing-lg);
	}

	.chart-options-row.end {
		display:flex;
		justify-content: end;
		margin-right: 1.25em;
		height: 4em;
		padding-top: 1.5em;
	}

	.breadcrumb-container {
		height: 2.5em;
	}
	.breadcrumb-label {
		color: var(--text-Secondary, #757575);

		/* Medium/Base */
		font-family: var(--font-family-Title, Roboto);
		font-size: var(--font-size-Base, 1em);
		font-style: normal;
		font-weight: 500;
		line-height: var(--font-line-height-Base, 1.5em); /* 150% */
		padding: 0 0.5em;
	}

	.btn-tertiary {
		border: 0;
		min-width: unset;
	}

	.btn-tertiary:disabled, .btn-tertiary:disabled:hover {
		border: 0;
		opacity: 1.0;
		text-decoration: none;
		cursor:default;
	}

</style>
